import { inject, Service } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { ModalController } from '@ionic/angular/standalone';
import { filter } from 'rxjs';
import { AppUpdateModalComponent } from '@modals';
import { ConfirmationService } from '@services';

const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

@Service()
export class AppUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly modalController = inject(ModalController);
  private readonly confirmationService = inject(ConfirmationService);

  private initialized = false;

  initialize(): void {
    if (this.initialized || !this.swUpdate.isEnabled) return;

    this.initialized = true;

    this.swUpdate.versionUpdates
      .pipe(filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'))
      .subscribe(() => {
        void this.promptUpdateAsync();
      });

    // A failed download is not fatal: the current version keeps running and the
    // next scheduled check retries the download.
    this.swUpdate.versionUpdates
      .pipe(filter((event) => event.type === 'VERSION_INSTALLATION_FAILED'))
      .subscribe((event) => console.error('Service worker failed to install the new version.', event));

    // The service worker can no longer serve the current version (e.g. cached
    // files were evicted). Recovering requires a full reload from the network.
    this.swUpdate.unrecoverable.subscribe(async () => {
      const confirmed = await this.confirmationService.confirmAsync({
        titleKey: 'pwa.unrecoverable.title',
        messageKey: 'pwa.unrecoverable.message',
        confirmTextKey: 'pwa.update.reload',
        cancelTextKey: 'general.close',
      });
      if (confirmed) this.reloadApp();
    });

    void this.checkForUpdateAsync();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void this.checkForUpdateAsync();
    });

    setInterval(() => void this.checkForUpdateAsync(), UPDATE_CHECK_INTERVAL_MS);
  }

  private reloadApp(): void {
    document.location.reload();
  }

  private async promptUpdateAsync(): Promise<void> {
    const modal = await this.modalController.create({
      component: AppUpdateModalComponent,
      backdropDismiss: false,
      canDismiss: (_data, role) => Promise.resolve(role === 'reload'),
    });

    await modal.present();
    const { role } = await modal.onWillDismiss();
    if (role === 'reload') this.reloadApp();
  }

  private async checkForUpdateAsync(): Promise<void> {
    try {
      await this.swUpdate.checkForUpdate();
    } catch (error: unknown) {
      // Expected when offline or the server is unreachable; retried on the next interval.
      console.warn('Service worker update check failed.', error);
    }
  }
}
